import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import dayjs from "dayjs";
import PrescriptionHeader from "../../Pages/ConsultationNotes/VitalTabs/ViewPrescription/PrescriptionHeader";
import VitalService from "../../../Services/VitalService";

const DietPlanViewModal = ({
  isOpen,
  onClose,
  selectedPatient,
  selectedPatientDetails,
  age,
  clinicDetails,
  doctorData,
  hospitalAccountData,
  formatTime12hr
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [weight, setWeight] = useState("NA");

  useEffect(() => {
    if (selectedPatient?.patientId) {
      VitalService.getPatientRecentVitals(selectedPatient.patientId)
        .then((res) => {
          if (res?.data && Array.isArray(res.data)) {
            const weightVital = res.data.find(v => v.vitalCode === "WE");
            if (weightVital?.value) {
              setWeight(`${weightVital.value} kg`);
              return;
            }
          }

          // Fallback to getVitalsData
          return VitalService.getVitalsData({
            patientId: selectedPatient.patientId,
            status: 1
          }).then((res2) => {
            if (res2?.data && Array.isArray(res2.data)) {
              const weightVital = res2.data.find(v => v.vitalCode === "WE");
              if (weightVital?.value) {
                setWeight(`${weightVital.value} kg`);
                return;
              }
            }
            setWeight("NA");
          });
        })
        .catch((err) => {
          console.error("Error fetching patient weight vital:", err);
          setWeight("NA");
        });
    } else {
      setWeight("NA");
    }
  }, [selectedPatient?.patientId]);

  if (!isOpen || !selectedPatient) return null;

  const getImageUrl = (imageName) => {
    if (!imageName) return "";
    if (imageName.startsWith("http://") || imageName.startsWith("https://")) {
      return imageName;
    }
    const baseUrl = process.env.REACT_APP_DIET_PLAN_IMAGE_URL;
    return `${baseUrl}${imageName}`;
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-xl shadow-xl w-[1000px] h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-base font-semibold text-[#164463]">Diet Patient Details</h2>
          <label onClick={onClose} className="text-gray-500 hover:text-red-500 cursor-pointer">
            <FaTimes />
          </label>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="border rounded-xl p-5 bg-white shadow-sm">
            <PrescriptionHeader
              clinicDetails={clinicDetails}
              doctorData={doctorData}
              hospitalAccountData={hospitalAccountData}
            />

            <hr className="my-6 border-gray-150" />

            <div className="flex justify-between text-sm text-gray-600">
              <div className="flex flex-wrap gap-x-8 gap-y-2">
                <p><span className="font-bold text-gray-800">Name:</span> {selectedPatientDetails?.patientName || `${selectedPatientDetails?.firstName || ''} ${selectedPatientDetails?.lastName || ''}` || selectedPatient.patientName || selectedPatient.name}</p>
                <p><span className="font-bold text-gray-800">Age/Gender:</span> {age || selectedPatient.age || "NA"}/{selectedPatientDetails?.gender || selectedPatient.gender || "NA"}</p>
                <p><span className="font-bold text-gray-800">Phone:</span> {selectedPatientDetails?.mobile || selectedPatient.mobileNumber || selectedPatient.mobile}</p>
                {weight && weight !== "NA" && (
                  <p><span className="font-bold text-gray-800">Weight:</span> {weight}</p>
                )}
              </div>
            </div>

            <p className="mt-2 text-sm text-gray-600">
              <span className="font-bold text-gray-800">Date Range:</span> {selectedPatient.fromDate} {selectedPatient.toDate ? ` to ${selectedPatient.toDate}` : ""}
            </p>

            <hr className="my-6 border-gray-150" />

            <div className="space-y-6">
              {Object.entries(
                ([...(selectedPatient?.patientDietPlanDetails || selectedPatient?.dietPlanDetails || [])])
                  .sort((a, b) => {
                    const dayA = parseInt(a.dayTitle.match(/\d+/)?.[0] || 0);
                    const dayB = parseInt(b.dayTitle.match(/\d+/)?.[0] || 0);
                    if (dayA !== dayB) return dayA - dayB;
                    return (a.sortOrder || 0) - (b.sortOrder || 0);
                  })
                  .reduce((acc, curr) => {
                    if (!acc[curr.dayTitle]) acc[curr.dayTitle] = [];
                    acc[curr.dayTitle].push(curr);
                    return acc;
                  }, {})
              ).map(([dayTitle, meals], dayIndex) => (
                <div key={dayIndex} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 bg-emerald-50/50 border-b border-emerald-100 flex justify-between items-center">
                    <h5 className="font-bold text-emerald-800">
                      {dayTitle} 
                      {selectedPatient.fromDate && (
                        <span className="ml-2 font-normal text-emerald-600 text-xs">
                          - {dayjs(selectedPatient.fromDate).add(parseInt(dayTitle.match(/\d+/)?.[0] || 1) - 1, 'day').format('dddd, DD MMM YYYY')}
                        </span>
                      )}
                    </h5>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50/50 text-gray-500 uppercase text-[10px] font-bold tracking-wider border-b border-gray-100">
                        <tr>
                          <th className="px-5 py-3 text-left">Time</th>
                          <th className="px-5 py-3 text-left">Type</th>
                          <th className="px-5 py-3 text-left">Meal</th>
                          <th className="px-5 py-3 text-center">Compliance</th>
                          <th className="px-5 py-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {meals.map((meal, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-4 text-gray-700 font-medium">{formatTime12hr(meal.mealTime)}</td>
                            <td className="px-5 py-4">
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-[10px] font-bold">{meal.mealType}</span>
                            </td>
                            <td className="px-5 py-4 text-gray-600">{meal.meal}</td>
                            <td className="px-5 py-4 text-center">
                              <span className="text-xs font-semibold text-gray-700">
                                {meal.compliance ? String(meal.compliance).toUpperCase() : "-"}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-center">
                              {meal.actionImageName ? (
                                <img
                                  src={getImageUrl(meal.actionImageName)}
                                  alt="Meal Upload"
                                  onClick={() => setPreviewImage(getImageUrl(meal.actionImageName))}
                                  className="w-12 h-12 object-cover rounded-lg cursor-pointer border border-gray-200 hover:scale-110 transition-all duration-200 shadow-sm mx-auto"
                                  title="Click to zoom"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://placehold.co/100x100?text=Error";
                                  }}
                                />
                              ) : (
                                <span className="text-gray-400 text-xs italic">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {meals[0]?.activity && (
                          <tr className="bg-amber-50/30 border-t border-gray-100">
                            <td className="px-5 py-3 font-bold text-amber-800 text-xs text-center">Activity</td>
                            <td colSpan="2" className="px-5 py-3 text-gray-600 text-xs">{meals[0].activity}</td>
                            <td className="px-5 py-3 text-center">
                              <span className="text-xs font-semibold text-amber-900">
                                {meals[0].activityStatus ? String(meals[0].activityStatus).toUpperCase() : "-"}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-center">
                              {meals[0].activityImageName ? (
                                <img
                                  src={getImageUrl(meals[0].activityImageName)}
                                  alt="Activity Upload"
                                  onClick={() => setPreviewImage(getImageUrl(meals[0].activityImageName))}
                                  className="w-10 h-10 object-cover rounded-lg cursor-pointer border border-gray-200 hover:scale-110 transition-all duration-200 shadow-sm mx-auto"
                                  title="Click to zoom"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://placehold.co/100x100?text=Error";
                                  }}
                                />
                              ) : (
                                <span className="text-gray-400 text-xs italic">-</span>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[60] p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-2xl w-full relative">
            <div className="bg-gray-50 border-b px-5 py-3 flex justify-between items-center">
              <h4 className="font-semibold text-gray-800 text-sm">Meal Verification Image</h4>
              <button
                onClick={() => setPreviewImage(null)}
                className="text-gray-400 hover:text-red-500 hover:bg-gray-100 p-1.5 rounded-full transition-colors"
              >
                <FaTimes size={16} />
              </button>
            </div>
            <div className="p-6 flex justify-center items-center bg-gray-100/50 max-h-[70vh] overflow-y-auto">
              <img
                src={previewImage}
                alt="Meal upload"
                className="max-h-[60vh] max-w-full rounded-lg object-contain shadow-sm border bg-white"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://placehold.co/600x400?text=Image+Not+Available";
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DietPlanViewModal;
